// Utilidades de DOM e formatação usadas por toda a interface.

import { iconNode } from "./icons.js";

export function el(tag, attrs = {}, ...children) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v === undefined || v === null || v === false) continue;
    if (k === "class") node.className = v;
    else if (k === "style" && typeof v === "object") Object.assign(node.style, v);
    else if (k.startsWith("on")) node.addEventListener(k.slice(2), v);
    else if (k === "dataset") Object.assign(node.dataset, v);
    else if (k === "value") node.value = v;
    else if (k === "checked" || k === "selected" || k === "disabled" || k === "readOnly") node[k] = !!v;
    else if (v === true) node.setAttribute(k, "");
    else node.setAttribute(k, v);
  }
  append(node, children);
  return node;
}

export function append(node, children) {
  for (const c of [children].flat(Infinity)) {
    if (c === null || c === undefined || c === false) continue;
    node.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return node;
}

export const $ = (id) => document.getElementById(id);

export const icon = (p) => iconNode(p?.icon, p?.kind === "database" ? "▦" : "📄");
export const titleOf = (p) => (p && p.title) || "Sem título";

export function debounce(fn, ms) {
  let t = null;
  let lastArgs = null;
  const wrapped = (...args) => {
    lastArgs = args;
    clearTimeout(t);
    t = setTimeout(() => { t = null; fn(...lastArgs); }, ms);
  };
  wrapped.flush = () => {
    if (t === null) return undefined;
    clearTimeout(t);
    t = null;
    return fn(...lastArgs);
  };
  wrapped.pending = () => t !== null;
  return wrapped;
}

export function clock(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${pad(m)}:${pad(s % 60)}`;
}

export const fmtDateTime = (ts) => new Date(ts).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
export const fmtDate = (iso) => {
  if (!iso) return "";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + "T12:00:00") : new Date(iso);
  return isNaN(d) ? String(iso) : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
};

export function relTime(ts) {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "agora";
  if (diff < 3_600_000) return `há ${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000) return `há ${Math.floor(diff / 3_600_000)} h`;
  return fmtDateTime(ts);
}

export function safeGet(key, fallback = null) {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : JSON.parse(v);
  } catch {
    return fallback;
  }
}

export function safeSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* sem armazenamento */ }
}

let toastTimer = null;
export function toast(msg, ms = 4500) {
  const t = $("toast");
  t.textContent = msg;
  t.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (t.hidden = true), ms);
}

// Menu flutuante simples ancorado num elemento ou numa posição.
// nested: abre por cima de outro popup (ex.: o menu de papel dentro do
// Compartilhar) sem fechar o de baixo.
export function popup(anchor, content, { align = "left", onClose, className = "", nested = false } = {}) {
  if (!nested) closePopups();
  const box = el("div", { class: "popup " + className + (nested ? " nested" : "") }, content);
  overlayHost().append(box);
  const r = anchor instanceof Element ? anchor.getBoundingClientRect() : { left: anchor.x, right: anchor.x, bottom: anchor.y, top: anchor.y };
  const w = box.offsetWidth;
  const h = box.offsetHeight;
  let left = align === "right" ? r.right - w : r.left;
  let top = r.bottom + 4;
  if (left + w > innerWidth - 8) left = innerWidth - w - 8;
  if (top + h > innerHeight - 8) top = Math.max(8, r.top - h - 4);
  box.style.left = Math.max(8, left) + "px";
  box.style.top = top + "px";
  // Se o conteúdo crescer depois (troca de aba num seletor), rola em vez de sair da tela.
  box.style.maxHeight = Math.max(160, innerHeight - top - 8) + "px";
  const close = () => {
    box.remove();
    document.removeEventListener("mousedown", outside, true);
    document.removeEventListener("keydown", esc, true);
    onClose?.();
  };
  const outside = (e) => {
    if (box.contains(e.target) || (anchor instanceof Element && anchor.contains(e.target))) return;
    // Clique num popup aberto por cima deste não fecha este.
    const other = e.target.closest?.(".popup.nested");
    if (other && other !== box && [...document.querySelectorAll(".popup")].indexOf(other) > [...document.querySelectorAll(".popup")].indexOf(box)) return;
    close();
  };
  const esc = (e) => {
    if (e.key !== "Escape" || [...document.querySelectorAll(".popup")].pop() !== box) return;
    e.stopPropagation();
    close();
  };
  setTimeout(() => {
    document.addEventListener("mousedown", outside, true);
    document.addEventListener("keydown", esc, true);
  });
  box.close = close;
  return box;
}

// Um <dialog> aberto com showModal fica numa camada acima de tudo; menus e
// seletores abertos de dentro dele precisam morar nele para aparecer na frente.
export function overlayHost() {
  return [...document.querySelectorAll("dialog[open]")].pop() || document.body;
}

export function closePopups() {
  for (const p of document.querySelectorAll(".popup")) p.close?.();
}

export function menu(anchor, items, opts) {
  const box = popup(anchor, items.filter(Boolean).map((it) => it === "-"
    ? el("div", { class: "menu-sep" })
    : el("button", { class: "menu-item" + (it.danger ? " danger" : ""), onclick: () => { box.close(); it.run(); } },
      it.icon ? el("span", { class: "mi-icon" }, it.icon) : null, el("span", {}, it.label),
      it.hint ? el("span", { class: "mi-hint" }, it.hint) : null)), { ...opts, className: "menu" });
  return box;
}

// Diálogo modal. `build(close)` devolve o conteúdo.
export function dialog(build, { wide = false, className = "" } = {}) {
  const dlg = el("dialog", { class: "dialog" + (wide ? " wide" : "") + " " + className });
  const close = () => dlg.close();
  append(dlg, build(close));
  dlg.addEventListener("close", () => dlg.remove());
  dlg.addEventListener("mousedown", (e) => { if (e.target === dlg) dlg.close(); });
  document.body.append(dlg);
  dlg.showModal();
  return dlg;
}

export function confirmDialog(text, { ok = "Confirmar", danger = false } = {}) {
  return new Promise((resolve) => {
    let answer = false;
    const d = dialog((close) => [
      el("p", { class: "confirm-text" }, text),
      el("div", { class: "dlg-actions" },
        el("button", { onclick: close }, "Cancelar"),
        el("button", { class: danger ? "danger-btn" : "primary", onclick: () => { answer = true; close(); } }, ok)),
    ]);
    d.addEventListener("close", () => resolve(answer));
  });
}

export const COLORS = ["default", "gray", "brown", "orange", "yellow", "green", "blue", "purple", "pink", "red"];

// append/replaceChildren ignoram null, undefined e false (como o el()), em vez
// de escrever "null" na tela quando um filho é condicional.
for (const name of ["append", "prepend", "replaceChildren"]) {
  const original = Element.prototype[name];
  Element.prototype[name] = function (...nodes) {
    return original.apply(this, nodes.filter((n) => n !== null && n !== undefined && n !== false));
  };
}
